/* ef-shop.js — the ported pages' interactive behaviour.
 *
 * The design prototype drove these with its own fake cart and in-memory state.
 * On Shopify the cart is real, so add-to-cart goes through /cart/add.js and
 * then hands off to the theme's existing drawer by dispatching ef:cart:added —
 * the drawer already knows how to refresh and open itself, and reimplementing
 * that would mean two cart UIs disagreeing.
 *
 * Everything here is progressive: with no JS the pages are complete documents,
 * the filters are links, and nothing is hidden.
 */
(function () {
  'use strict';

  var DATA = {}, PAIRS = {};
  try {
    var el = document.getElementById('ef-catalog');
    if (el) {
      var raw = JSON.parse(el.textContent);
      DATA = raw.products || raw;      /* tolerate the older flat shape */
      PAIRS = raw.pairs || {};
    }
  } catch (e) { /* a broken payload must not take the page down */ }

  var money = function (n) {
    return 'RM ' + Number(n).toLocaleString('en-MY');
  };

  /* ----------------------------------------------------------- add to cart */
  function addToCart(btn, pid) {
    var item = DATA[pid];
    if (!item || !item.variant) return;
    addVariant(btn, item.variant);
  }

  /* Collection cards are rendered from live Shopify products, not from the
     designed catalogue payload, so they carry a variant id directly and
     never appear in DATA. Same request, same event, same drawer. */
  function addVariant(btn, variantId) {
    if (!variantId) return;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      })
      .then(function () {
        /* The drawer owns what happens next. */
        document.dispatchEvent(new CustomEvent('ef:cart:added'));
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = label; }, 1600);
      })
      .catch(function () {
        /* Say what went wrong and leave the button usable. Silently doing
           nothing on a buy button is the worst available outcome. */
        btn.textContent = 'Try again';
        setTimeout(function () { btn.textContent = label; }, 2200);
      })
      .finally(function () { btn.disabled = false; });
  }

  /* A card whose truth was decided in Liquid. The designed catalogue resolves
     its buttons from DATA at runtime; a collection card cannot, because the
     product may not be one of the 28 the design draws. Liquid already knows
     whether the product is available and how many variants it has, so the
     button ships already correct and this only has to send the request. */
  function initVariantButtons(root) {
    root.querySelectorAll('[data-add-variant]').forEach(function (btn) {
      if (btn.dataset.efBound) return;
      btn.dataset.efBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        addVariant(btn, btn.getAttribute('data-add-variant'));
      });
    });
  }

  function initBuyButtons(root) {
    root.querySelectorAll('[data-add]').forEach(function (btn) {
      if (btn.dataset.efBound) return;
      btn.dataset.efBound = '1';
      var pid = btn.getAttribute('data-add');
      var item = DATA[pid];

      if (!item || (!item.variant && !item.choose)) {
        /* Not sellable — either no store listing or no available variant. The
           button becomes an enquiry route rather than a dead control. */
        btn.textContent = 'Notify me';
        btn.setAttribute('data-ef-enquire', pid);
        btn.addEventListener('click', function () { location.href = '/pages/contact'; });
        return;
      }
      if (item.choose) {
        /* More than one variant: the customer picks on the product page. A card
           cannot honestly choose 35L or 55L for them. */
        btn.textContent = 'Choose options';
        btn.addEventListener('click', function () { location.href = item.url; });
        return;
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        addToCart(btn, pid);
      });
    });
  }

  /* ------------------------------------------------------ catalogue filters */
  function initCatalogue(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('.ef-pcard'));
    if (!cards.length) return;
    var series = root.querySelectorAll('[data-filter]');
    var tiers  = root.querySelectorAll('[data-tier]');
    if (!series.length && !tiers.length) return;

    var state = { series: 'all', tier: 'all' };

    function pidOf(card) {
      var b = card.querySelector('[data-add]');
      return b && b.getAttribute('data-add');
    }

    function apply() {
      var shown = 0;
      cards.forEach(function (card) {
        var d = DATA[pidOf(card)] || {};
        var ok = (state.series === 'all' || d.series === state.series) &&
                 (state.tier === 'all' || d.tier === state.tier);
        card.hidden = !ok;
        if (ok) shown++;
      });
      /* Tier chips belong to the selected series; leaving DELTA's tiers up
         while RIVER is selected offers filters that match nothing, which reads
         as a broken page rather than an empty result. */
      tiers.forEach(function (t) {
        var val = t.getAttribute('data-tier');
        if (val === 'all') return;
        t.hidden = !Object.keys(DATA).some(function (k) {
          return DATA[k].tier === val &&
                 (state.series === 'all' || DATA[k].series === state.series);
        });
      });
      return shown;
    }

    function select(nodes, node, key, value) {
      nodes.forEach(function (n) {
        var on = n === node;
        n.setAttribute('aria-current', on ? 'true' : 'false');
        n.classList.toggle('is-on', on);
      });
      state[key] = value;
      if (key === 'series') {
        /* A tier from the previous series matches nothing in the new one. */
        state.tier = 'all';
        tiers.forEach(function (t) {
          var on = t.getAttribute('data-tier') === 'all';
          t.setAttribute('aria-current', on ? 'true' : 'false');
          t.classList.toggle('is-on', on);
        });
      }
      apply();
    }

    series.forEach(function (n) {
      n.addEventListener('click', function (e) {
        e.preventDefault();
        select(series, n, 'series', n.getAttribute('data-filter'));
        writeUrl();
      });
    });
    tiers.forEach(function (n) {
      n.addEventListener('click', function (e) {
        e.preventDefault();
        select(tiers, n, 'tier', n.getAttribute('data-tier'));
        writeUrl();
      });
    });

    /* ---- deep links -------------------------------------------------------
       The tabs were click-only (href="#"), so this page could only ever be
       entered showing everything. "Shop all solar" therefore landed on the
       same undifferentiated catalogue as "Shop all power stations", which is
       what it was supposed to be an alternative to.

       ?series=Solar (optionally &tier=) now arrives pre-filtered, and every
       click rewrites the address bar, so a filtered view is linkable, and the
       back button steps through the filters instead of leaving the page.
       replaceState, not pushState, on the click path: a filter is a view of
       one page, not a new one, and stacking history entries for each chip
       makes Back feel broken. */
    function writeUrl() {
      if (!window.history || !history.replaceState) return;
      var q = [];
      if (state.series !== 'all') q.push('series=' + encodeURIComponent(state.series));
      if (state.tier !== 'all') q.push('tier=' + encodeURIComponent(state.tier));
      history.replaceState(null, '',
        location.pathname + (q.length ? '?' + q.join('&') : '') + location.hash);
    }

    function fromUrl() {
      var p = new URLSearchParams(location.search);
      var want = p.get('series');
      var node = want && Array.prototype.slice.call(series).filter(function (n) {
        return n.getAttribute('data-filter') === want;
      })[0];
      /* An unknown series is left alone rather than showing nothing: a stale
         link to a retired family should show the catalogue, not an empty grid. */
      if (node) select(series, node, 'series', want);
      var wantTier = p.get('tier');
      var tnode = wantTier && Array.prototype.slice.call(tiers).filter(function (n) {
        return n.getAttribute('data-tier') === wantTier;
      })[0];
      if (tnode) select(tiers, tnode, 'tier', wantTier);
    }

    apply();
    fromUrl();
  }

  /* ------------------------------------------------------- price list sort */
  function initSort(root) {
    var buttons = root.querySelectorAll('[data-sort]');
    if (!buttons.length) return;
    var table = root.querySelector('.ef-table') || root.querySelector('table');
    if (!table || !table.tBodies.length) return;
    var body = table.tBodies[0];
    var dir = {};

    /* The sort button sits inside the header cell for its own column, so the
       column index comes from the DOM rather than a hand-kept mapping that
       would silently rot the first time a column moves. */
    function columnOf(btn) {
      var cell = btn.closest('th, td');
      if (!cell) return 0;
      return Array.prototype.indexOf.call(cell.parentNode.children, cell);
    }

    function cellText(row, i) {
      var c = row.children[i];
      return c ? c.textContent.trim() : '';
    }

    /* Decide the column's type ONCE from its contents, then coerce every cell
       to it. Judging cell by cell lets a stray "Price on request" fall through
       to string comparison in a column of ringgit, and it sorts above RM 30,999
       because "p" beats "3" — the row with no price ends up looking like the
       most expensive thing in the shop. */
    function columnIsNumeric(col) {
      var rows = Array.prototype.slice.call(body.rows), num = 0, seen = 0;
      rows.forEach(function (r) {
        var s = cellText(r, col);
        if (!s || s === '\u2014') return;
        seen++;
        if (/[0-9]/.test(s) && !isNaN(parseFloat(s.replace(/[^0-9.]/g, '')))) num++;
      });
      return seen > 0 && num / seen > 0.6;
    }

    function comparable(s, numeric) {
      /* An em dash, a blank, or prose in a numeric column all mean "no value
         here" and sink to the bottom in BOTH directions. */
      if (!s || s === '\u2014') return null;
      if (!numeric) return s.toLowerCase();
      var n = parseFloat(s.replace(/[^0-9.]/g, ''));
      return isNaN(n) ? null : n;
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var key = b.getAttribute('data-sort');
        var col = columnOf(b);
        dir[key] = dir[key] === 'asc' ? 'desc' : 'asc';
        var sign = dir[key] === 'asc' ? 1 : -1;
        var numeric = columnIsNumeric(col);
        var rows = Array.prototype.slice.call(body.rows);
        rows.sort(function (x, y) {
          var a = comparable(cellText(x, col), numeric),
              c = comparable(cellText(y, col), numeric);
          if (a === null && c === null) return 0;
          if (a === null) return 1;          /* blanks last, both directions */
          if (c === null) return -1;
          if (typeof a === 'number' && typeof c === 'number') return (a - c) * sign;
          return String(a).localeCompare(String(c), 'en-MY') * sign;
        });
        rows.forEach(function (r) { body.appendChild(r); });
        buttons.forEach(function (o) {
          var cell = o.closest('th');
          if (cell) cell.setAttribute('aria-sort', 'none');
        });
        var own = b.closest('th');
        if (own) own.setAttribute('aria-sort', dir[key] === 'asc' ? 'ascending' : 'descending');
      });
    });
  }

  /* ------------------------------------------------------- compare pickers */
  function initComparePicker(root) {
    var a = root.querySelector('#cmp-a'), b = root.querySelector('#cmp-b');
    if (!a || !b) return;

    function go() {
      var key = a.value + '|' + b.value;
      var url = PAIRS[key] || PAIRS[b.value + '|' + a.value];
      /* Only navigate to a pair that exists. Picking two models with no page
         leaves you where you are rather than on a 404 — the six pairs are the
         ones with written verdicts, and inventing a seventh URL would promise
         a page nobody wrote. */
      if (url && url !== location.pathname) location.href = url;
    }
    a.addEventListener('change', go);
    b.addEventListener('change', go);

    var swap = root.querySelector('[data-cmpswap]');
    if (swap) {
      swap.addEventListener('click', function (e) {
        e.preventDefault();
        var url = PAIRS[b.value + '|' + a.value];
        if (url) location.href = url;
      });
    }
  }

  /* ------------------------------------------------- dealer / service filter */
  function initLocate(root) {
    var controls = root.querySelectorAll('[data-loc]');
    var cards    = root.querySelectorAll('[data-loc-card]');
    if (!controls.length || !cards.length) return;
    var regions = root.querySelectorAll('[data-loc-region]');

    var count = root.querySelector('[data-loc-count]');
    /* The unfiltered figure counts branches too, which the cards do not show
       individually — so keep it for "all" and count cards for a filter rather
       than silently changing what the number means. */
    var totalLabel = count ? count.textContent.trim() : '';

    function apply(kind) {
      var shown = 0;
      cards.forEach(function (c) {
        var kinds = (c.getAttribute('data-loc-kind') || '').split(' ');
        c.hidden = !(kind === 'all' || kinds.indexOf(kind) !== -1);
        if (!c.hidden) shown++;
      });
      if (count) {
        count.textContent = kind === 'all' ? totalLabel
          : shown + (shown === 1 ? ' location' : ' locations');
      }
      /* A region whose every card is filtered out hides with them. Left in, the
         heading reads as a section that failed to load rather than a region
         with no service point in it. */
      regions.forEach(function (r) {
        var inRegion = r.querySelectorAll('[data-loc-card]');
        if (!inRegion.length) return;
        r.hidden = !Array.prototype.some.call(inRegion, function (c) { return !c.hidden; });
      });
    }

    controls.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var kind = btn.getAttribute('data-loc');
        controls.forEach(function (o) {
          var on = o === btn;
          o.classList.toggle('is-on', on);
          o.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        apply(kind);
      });
    });

    /* State the starting state rather than leaving it implied. */
    controls.forEach(function (o) {
      var on = o.getAttribute('data-loc') === 'all';
      o.classList.toggle('is-on', on);
      o.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    apply('all');
  }

  /* --------------------------------------------------- pick two to compare */
  function initCatPick(root) {
    var picks = Array.prototype.slice.call(root.querySelectorAll('[data-catpick]'));
    if (!picks.length) return;

    function pairUrl(a, b) { return PAIRS[a + '|' + b] || PAIRS[b + '|' + a]; }

    /* A pair is comparable when a written page exists for it. That was the
       original rule; Phase 2 widened it to any two records of the same kind and
       drew a table for the rest, and that table was dropped on 2026-08-31. With
       nothing to render, offering a pick that cannot be answered would promise
       a comparison that does not exist — which is what the widening was for. */
    function comparable(a, b) { return a !== b && !!pairUrl(a, b); }
    function compareUrl(a, b) { return pairUrl(a, b); }
    var chosen = null;
    var labels = picks.map(function (b) { return b.textContent.trim(); });

    function reset() {
      chosen = null;
      picks.forEach(function (b, i) {
        b.disabled = false;
        b.textContent = labels[i];
        b.classList.remove('is-on');
        b.setAttribute('aria-pressed', 'false');
      });
    }

    function choose(btn) {
      chosen = btn.getAttribute('data-catpick');
      picks.forEach(function (b, i) {
        var id = b.getAttribute('data-catpick');
        if (b === btn) {
          b.classList.add('is-on');
          b.setAttribute('aria-pressed', 'true');
          b.textContent = 'Selected';
          return;
        }
        /* Only offer a second pick the tool can actually answer. That is now
           any comparable record rather than the six written pairs; a solar
           panel against a fridge still says so before the first click rather
           than after the second. */
        var ok = comparable(chosen, id);
        b.disabled = !ok;
        b.textContent = ok ? 'Compare with this' : 'No comparison';
      });
    }

    picks.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-catpick');
        if (chosen === id) { reset(); return; }
        if (!chosen) { choose(btn); return; }
        if (comparable(chosen, id)) location.href = compareUrl(chosen, id);
        else reset();
      });
    });
    reset();
  }

  /* --------------------------------------------------------- panel switches */
  function initPanels(root) {
    /* A control names a group; its value shows one member and hides the rest.
       Merdeka's greetings and bundle kits are both this shape. */
    [['data-mklang', 'data-mklang-panel'],
     ['data-bundle', 'data-bundle-panel']].forEach(function (pair) {
      var controls = root.querySelectorAll('[' + pair[0] + ']');
      var panels = root.querySelectorAll('[' + pair[1] + ']');
      if (!controls.length || !panels.length) return;

      function show(v) {
        panels.forEach(function (p) { p.hidden = p.getAttribute(pair[1]) !== v; });
        controls.forEach(function (c) {
          var on = c.getAttribute(pair[0]) === v;
          c.classList.toggle('is-on', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      controls.forEach(function (c) {
        c.addEventListener('click', function (e) {
          e.preventDefault();
          show(c.getAttribute(pair[0]));
        });
      });
      /* Open on the FIRST CONTROL, not the first panel. Panel order is whatever
         the render happened to emit; control order is the designed order, and
         on a Merdeka page that means Bahasa Melayu leads rather than English. */
      show(controls[0].getAttribute(pair[0]));
    });
  }

  function init(root) {
    root = root || document;
    initBuyButtons(root);
    initVariantButtons(root);
    initCatalogue(root);
    initSort(root);
    initComparePicker(root);
    initLocate(root);
    initPanels(root);
    initCatPick(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  /* Theme editor: a re-rendered section needs re-binding. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
